import React, { useState, useEffect, useMemo } from 'react';
import { t, Trans } from '@lingui/macro';
import { Mail, User as UserIcon, Copy, RefreshCw, Trash2, Check, Loader2, ChevronUp, Crown, Shield, UserX, Search, Eye, DoorOpen, Users } from 'lucide-react';
import { RoomSimplified } from '../../../models/Room';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from '../../../components/ui/dropdown-menu';
import { Workspace, WorkspaceMember, WorkspaceMemberRole, getInvitationPolicy } from '../../../models/Workspace';
import { useMyPermissions } from '../../../context/WorkspacePermissionContext';
import { Fire } from '../../../services/FireClient';
import { Util } from '../../../services/Utils/Util';
import { useToast } from '../../../components/ui/use-toast';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Avatar, AvatarFallback, AvatarImage } from '../../../components/ui/avatar';
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from '../../../components/ui/card';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from "../../../components/ui/dialog";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
    AlertDialogTrigger,
} from "../../../components/ui/alert-dialog";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../../../components/ui/select";
import { Badge } from "../../../components/ui/badge";
import { Popover, PopoverContent, PopoverTrigger } from "../../../components/ui/popover";
import { UUID } from '../../../models/local/Uid';
import { copyToClipboard as clipboardWrite } from "../../../services/Utils/ClipboardUtil";
import { UtilApp } from '../../../services/Utils/UtilApp';
import { getDisplayName } from '../../../services/Utils/userDisplayName';
import { My } from '../../../services/My';
import { IconChevronDown, IconLink, IconMoreHorizontal } from '../../components/FigmaIcons';

interface WSAdminMembersProps {
    workspace: Workspace;
}

const WSAdminMembers: React.FC<WSAdminMembersProps> = ({ workspace }) => {
    const [members, setMembers] = useState<(WorkspaceMember & { lastActiveAt?: Date })[]>([]);
    const [rooms, setRooms] = useState<RoomSimplified[]>([]);
    const [inProgress, setInProgress] = useState(false);
    const [removingId, setRemovingId] = useState<string | null>(null);
    const { toast } = useToast();
    const [invitationDialogOpen, setInvitationDialogOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [memberRoomsDialog, setMemberRoomsDialog] = useState<{ open: boolean; memberId: string; memberName: string }>({ open: false, memberId: '', memberName: '' });
    const [expandedRoomId, setExpandedRoomId] = useState<string | null>(null);

    // 선택된 멤버가 참여한 방 목록
    const memberRooms = useMemo(() => {
        if (!memberRoomsDialog.open || !memberRoomsDialog.memberId) return [];
        return rooms.filter(r => r.userIds?.includes(memberRoomsDialog.memberId));
    }, [memberRoomsDialog, rooms]);

    // 멤버 ID Set (워크스페이스에 가입된 모든 사용자)
    const memberIdSet = useMemo(() => new Set(members.map(m => m.id)), [members]);
    // 게스트 ID Set (워크스페이스에서 guest 역할인 사용자)
    const guestIdSet = useMemo(() => new Set(members.filter(m => m.role === 'guest').map(m => m.id)), [members]);

    const filteredMembers = members.filter(m => {
        if (!searchQuery.trim()) return true;
        const q = searchQuery.toLowerCase();
        return m.user.name?.toLowerCase().includes(q) || m.user.email?.toLowerCase().includes(q);
    });

    const isOwner = workspace.owner?.id === My.user?.id;
    // Context에서 permissions 가져오기 (WorkspaceViewPage에서 Provider로 전달)
    const myPermissions = useMyPermissions();
    const [changingRoleId, setChangingRoleId] = useState<string | null>(null);

    useEffect(() => {
        loadMembers();
    }, [workspace.id]);

    const canManageMembers = isOwner || myPermissions?.canRemoveMembers;
    const canChangeRoles = isOwner || myPermissions?.canChangeRole;
    const canInvite = isOwner || myPermissions?.canInviteMembers;

    const loadMembers = async () => {
        setInProgress(true);
        try {
            const [list, roomRes] = await Promise.all([
                Fire.ws.getWorkspaceMembers(workspace.id),
                Fire.ws.getWorkspaceRooms({ workspaceId: workspace.id })
            ]);
            setMembers(list);
            setRooms(roomRes.rooms);
        } catch (e) {
            console.error(e);
            toast({ title: t`Failed to load members` });
        } finally {
            setInProgress(false);
        }
    };

    const removeMember = async (targetUserId: string, targetName: string) => {
        setRemovingId(targetUserId);
        try {
            const res = await Fire.ws.removeWorkspaceMember({
                workspaceId: workspace.id,
                targetUserId
            });
            if (res.status === 'SUCCESS') {
                toast({ title: t`${targetName} has been removed from the workspace.` });
                loadMembers();
            } else if (res.status === 'CANNOT_REMOVE_OWNER') {
                toast({ title: t`Cannot remove the workspace owner.` });
            } else if (res.status === 'ADMIN_ONLY_BY_OWNER') {
                toast({ title: t`Only the owner can remove admins.` });
            } else {
                toast({ title: t`Failed to remove member.` });
            }
        } catch (e) {
            console.error(e);
            toast({ title: t`Failed to remove member.` });
        } finally {
            setRemovingId(null);
        }
    };

    const handleRoleChange = async (targetUserId: string, newRole: 'admin' | 'member' | 'guest') => {
        setChangingRoleId(targetUserId);
        try {
            const res = await Fire.ws.changeMemberRole({
                workspaceId: workspace.id,
                targetUserId,
                newRole
            });
            if (res.status === 'SUCCESS') {
                toast({ title: t`Role changed successfully.` });
                loadMembers();
            } else if (res.status === 'CANNOT_CHANGE_OWNER') {
                toast({ title: t`Cannot change the owner's role.` });
            } else if (res.status === 'CANNOT_CHANGE_SELF') {
                toast({ title: t`Cannot change your own role.` });
            } else if (res.status === 'NOT_AUTHORIZED') {
                toast({ title: t`You are not authorized to change roles.` });
            } else {
                toast({ title: t`Failed to change role.` });
            }
        } catch (e) {
            console.error(e);
            toast({ title: t`Failed to change role.` });
        } finally {
            setChangingRoleId(null);
        }
    };

    const canRemove = (member: WorkspaceMember) => {
        if (!canManageMembers) return false;
        // owner 자신은 삭제 불가
        if (member.id === workspace.owner?.id) return false;
        // 자기 자신은 삭제 불가 (탈퇴는 별도 기능)
        if (member.id === My.user?.id) return false;
        // admin 멤버는 owner만 삭제 가능
        if (member.role === 'admin' && !isOwner) return false;
        return true;
    };

    const canChangeRole = (member: WorkspaceMember) => {
        if (!canChangeRoles) return false;
        if (member.id === workspace.owner?.id) return false;
        if (member.id === My.user?.id) return false;
        // admin 역할 변경은 owner만
        if (member.role === 'admin' && !isOwner) return false;
        return true;
    };

    const getRoleBadge = (member: WorkspaceMember) => {
        if (member.id === workspace.owner?.id) {
            return <Badge className="bg-amber-100 text-amber-700 border-amber-200 text-meta-sm px-1.5 h-5 gap-0.5"><Crown className="w-3 h-3" /><Trans>Owner</Trans></Badge>;
        }
        const role = member.role || 'member';
        if (role === 'admin') {
            return <Badge className="bg-blue-100 text-blue-700 border-blue-200 text-meta-sm px-1.5 h-5 gap-0.5"><Shield className="w-3 h-3" /><Trans>Admin</Trans></Badge>;
        }
        if (role === 'guest') {
            return <Badge className="bg-muted text-muted-foreground border-border text-meta-sm px-1.5 h-5 gap-0.5"><Eye className="w-3 h-3" /><Trans>Guest</Trans></Badge>;
        }
        return <Badge variant="outline" className="text-meta-sm px-1.5 h-5 gap-0.5 font-normal text-muted-foreground"><UserIcon className="w-3 h-3" /><Trans>Member</Trans></Badge>;
    };

    const [removeTarget, setRemoveTarget] = useState<{ id: string; name: string } | null>(null);

    const MemberActions = ({ member }: { member: typeof members[0] }) => {
        const showChangeRole = canChangeRole(member);
        const showRemove = canRemove(member);
        const memberRoomCount = rooms.filter(r => r.userIds?.includes(member.id)).length;

        return (
            <DropdownMenu>
                <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon" className="h-7 w-7">
                        {(changingRoleId === member.id || removingId === member.id)
                            ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                            : <IconMoreHorizontal className="h-4 w-4" />}
                    </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                    <DropdownMenuItem
                        onClick={() => setMemberRoomsDialog({ open: true, memberId: member.id, memberName: getDisplayName(member.user) })}
                    >
                        <DoorOpen className="w-3.5 h-3.5 mr-2" /><Trans>View Channels</Trans>
                        <Badge variant="secondary" className="ml-auto text-meta-sm h-4 px-1.5">{memberRoomCount}</Badge>
                    </DropdownMenuItem>
                    {(showChangeRole || showRemove) && <DropdownMenuSeparator />}
                    {showChangeRole && (
                        <>
                            <DropdownMenuLabel className="text-xs text-muted-foreground"><Trans>Change Role</Trans></DropdownMenuLabel>
                            {isOwner && (
                                <DropdownMenuItem
                                    onClick={() => handleRoleChange(member.id, 'admin')}
                                    disabled={member.role === 'admin'}
                                >
                                    <Shield className="w-3.5 h-3.5 mr-2" /><Trans>Admin</Trans>
                                    {member.role === 'admin' && <Check className="w-3.5 h-3.5 ml-auto" />}
                                </DropdownMenuItem>
                            )}
                            <DropdownMenuItem
                                onClick={() => handleRoleChange(member.id, 'member')}
                                disabled={(member.role || 'member') === 'member'}
                            >
                                <UserIcon className="w-3.5 h-3.5 mr-2" /><Trans>Member</Trans>
                                {(member.role || 'member') === 'member' && <Check className="w-3.5 h-3.5 ml-auto" />}
                            </DropdownMenuItem>
                            <DropdownMenuItem
                                onClick={() => handleRoleChange(member.id, 'guest')}
                                disabled={member.role === 'guest'}
                            >
                                <Eye className="w-3.5 h-3.5 mr-2" /><Trans>Guest</Trans>
                                {member.role === 'guest' && <Check className="w-3.5 h-3.5 ml-auto" />}
                            </DropdownMenuItem>
                        </>
                    )}
                    {showChangeRole && showRemove && <DropdownMenuSeparator />}
                    {showRemove && (
                        <DropdownMenuItem
                            className="text-destructive focus:text-destructive focus:bg-destructive/10"
                            onClick={() => setRemoveTarget({ id: member.id, name: getDisplayName(member.user) })}
                        >
                            <UserX className="w-3.5 h-3.5 mr-2" /><Trans>Remove</Trans>
                        </DropdownMenuItem>
                    )}
                </DropdownMenuContent>
            </DropdownMenu>
        );
    };

    return (
        <div className="w-full p-2 sm:p-4 md:p-6 lg:p-8 space-y-6">
            {/* Header */}
            <div className="flex items-start justify-between gap-2 sm:gap-4 px-2 sm:px-0">
                <div className="flex items-center gap-2 sm:gap-4">
                    <div className="hidden sm:block p-3 rounded-xl bg-muted border border-border">
                        <UserIcon className="h-6 w-6 text-card-foreground" />
                    </div>
                    <div>
                        <h2 className="text-lg sm:text-2xl font-bold tracking-tight"><Trans>Members</Trans></h2>
                        <p className="hidden sm:block text-sm text-muted-foreground"><Trans>Manage who has access to this workspace.</Trans></p>
                    </div>
                </div>
                {canInvite && <InvitationDialog workspace={workspace} open={invitationDialogOpen} onOpenChange={setInvitationDialogOpen} />}
            </div>

            {/* 검색 */}
            <div className="relative px-2 sm:px-0">
                <Search className="absolute left-4 sm:left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                    placeholder={t`Search by name or email`}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-9 h-9"
                />
            </div>

            {/* 멤버 수 */}
            <div className="text-sm text-muted-foreground px-2 sm:px-0">
                <Trans>Member List</Trans> ({filteredMembers.length}/{members.length})
            </div>

            {inProgress ? (
                <div className="flex items-center justify-center py-12">
                    <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                    <span className="ml-2 text-sm text-muted-foreground"><Trans>Loading...</Trans></span>
                </div>
            ) : filteredMembers.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground text-sm">
                    <Trans>No members found</Trans>
                </div>
            ) : (
                <>
                    {/* 모바일 레이아웃 — 카드형 리스트 */}
                    <div className="sm:hidden divide-y">
                        {filteredMembers.map((member) => (
                            <div key={member.id} className="flex items-center gap-3 py-3 px-2">
                                <Avatar className="h-9 w-9 shrink-0">
                                    <AvatarImage src={member.user.photoUrl || undefined} alt={getDisplayName(member.user)} />
                                    <AvatarFallback><UserIcon className="h-4 w-4" /></AvatarFallback>
                                </Avatar>
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-1.5">
                                        <span className="text-sm font-medium truncate">{getDisplayName(member.user)}</span>
                                        {getRoleBadge(member)}
                                    </div>
                                    <div className="text-xs text-muted-foreground truncate">{member.user.email}</div>
                                </div>
                                <MemberActions member={member} />
                            </div>
                        ))}
                    </div>

                    {/* 데스크톱/태블릿 레이아웃 — 테이블 */}
                    <div className="hidden sm:block overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="border-b text-left text-muted-foreground">
                                    <th className="py-2 px-3 font-medium"><Trans>Name</Trans></th>
                                    <th className="py-2 px-3 font-medium"><Trans>Email</Trans></th>
                                    <th className="py-2 px-3 font-medium"><Trans>Role</Trans></th>
                                    <th className="py-2 px-3 font-medium"><Trans>Registered</Trans></th>
                                    <th className="py-2 px-3 font-medium"><Trans>Last Active</Trans></th>
                                    <th className="py-2 px-3 font-medium w-10"></th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredMembers.map((member) => (
                                    <tr key={member.id} className="border-b last:border-0 hover:bg-muted/50 transition-colors">
                                        <td className="py-2.5 px-3">
                                            <div className="flex items-center gap-2.5">
                                                <Avatar className="h-8 w-8 shrink-0">
                                                    <AvatarImage src={member.user.photoUrl || undefined} />
                                                    <AvatarFallback><UserIcon className="h-3.5 w-3.5" /></AvatarFallback>
                                                </Avatar>
                                                <span className="font-medium truncate max-w-[160px]">{getDisplayName(member.user)}</span>
                                            </div>
                                        </td>
                                        <td className="py-2.5 px-3 text-muted-foreground truncate max-w-[200px]">{member.user.email}</td>
                                        <td className="py-2.5 px-3">{getRoleBadge(member)}</td>
                                        <td className="py-2.5 px-3 text-muted-foreground text-xs whitespace-nowrap">{member.created ? UtilApp.dateStrToYmd(member.created) : '-'}</td>
                                        <td className="py-2.5 px-3 text-muted-foreground text-xs whitespace-nowrap">{member.lastActiveAt ? UtilApp.dateStrToTimeAndDay(member.lastActiveAt, My.user.lang) : '-'}</td>
                                        <td className="py-2.5 px-3"><MemberActions member={member} /></td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </>
            )}

            {/* 멤버 삭제 확인 다이얼로그 */}
            <AlertDialog open={!!removeTarget} onOpenChange={(open) => !open && setRemoveTarget(null)}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle><Trans>Delete member?</Trans></AlertDialogTitle>
                        <AlertDialogDescription>
                            <Trans>The member will also be removed from all chat channels in this workspace.</Trans>
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel><Trans>Cancel</Trans></AlertDialogCancel>
                        <AlertDialogAction
                            onClick={() => {
                                if (removeTarget) {
                                    removeMember(removeTarget.id, removeTarget.name);
                                    setRemoveTarget(null);
                                }
                            }}
                            className="bg-destructive hover:bg-destructive"
                        >
                            <Trans>Remove</Trans>
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            {/* 멤버 참여 방 목록 모달 */}
            <Dialog open={memberRoomsDialog.open} onOpenChange={(open) => setMemberRoomsDialog(prev => ({ ...prev, open }))}>
                <DialogContent className="w-[95vw] sm:max-w-md p-0 overflow-hidden">
                    <DialogHeader className="p-4 pb-2">
                        <DialogTitle className="flex items-center gap-2">
                            <Users className="w-5 h-5" />
                            {memberRoomsDialog.memberName}
                        </DialogTitle>
                        <DialogDescription>
                            <Trans>{memberRooms.length} channels</Trans>
                        </DialogDescription>
                    </DialogHeader>
                    <div className="max-h-[60vh] overflow-y-auto px-4 pb-4 space-y-1">
                        {memberRooms.length === 0 ? (
                            <div className="text-center py-8 text-muted-foreground text-sm">
                                <Trans>No channels</Trans>
                            </div>
                        ) : memberRooms.map(room => {
                            const isCreator = room.creatorId === memberRoomsDialog.memberId;
                            return (
                                <div key={room.id} className="flex items-center gap-3 p-2.5 rounded-lg hover:bg-muted/50 transition-colors">
                                    <div className="w-8 h-8 rounded-lg bg-muted flex items-center justify-center text-muted-foreground text-xs font-bold shrink-0">
                                        {room.name?.charAt(0)?.toUpperCase() || '#'}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="font-medium text-sm truncate flex items-center gap-1.5">
                                            # {room.name || (room.isOneToOne ? t`1:1 Chat` : t`Unnamed`)}
                                            {isCreator && <Crown className="w-3 h-3 text-amber-500 shrink-0" />}
                                        </div>
                                    </div>
                                    {room.isOneToOne && (
                                        <Badge variant="outline" className="text-meta-sm h-4 px-1.5 shrink-0">1:1</Badge>
                                    )}
                                    <Popover>
                                        <PopoverTrigger asChild>
                                            <Button variant="ghost" size="sm" className="h-6 px-2 text-xs text-muted-foreground hover:text-foreground gap-1">
                                                <Users className="w-3 h-3" />
                                                {room.users?.length || 0}
                                            </Button>
                                        </PopoverTrigger>
                                        <PopoverContent className="w-64 p-0" align="end" side="left">
                                            <div className="p-2.5 border-b text-xs font-medium text-muted-foreground">
                                                <Trans>Participants</Trans> ({room.users?.length || 0})
                                            </div>
                                            <div className="max-h-48 overflow-y-auto divide-y divide-gray-50">
                                                {room.users?.map(u => {
                                                    const isGuest = guestIdSet.has(u.id);
                                                    const isRoomCreator = room.creatorId === u.id;
                                                    return (
                                                        <div key={u.id} className="flex items-center gap-2 px-3 py-1.5">
                                                            <Avatar className="h-5 w-5 shrink-0">
                                                                <AvatarImage src={u.photoUrl ?? undefined} />
                                                                <AvatarFallback className="text-meta-sm">{getDisplayName(u).substring(0, 1)}</AvatarFallback>
                                                            </Avatar>
                                                            <span className="text-xs truncate flex-1 flex items-center gap-1">
                                                                {getDisplayName(u)}
                                                                {isRoomCreator && <Crown className="w-2.5 h-2.5 text-amber-500 shrink-0" />}
                                                            </span>
                                                            {isGuest && (
                                                                <Badge variant="secondary" className="text-meta-sm h-3.5 px-1 shrink-0">
                                                                    <Trans>Guest</Trans>
                                                                </Badge>
                                                            )}
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </PopoverContent>
                                    </Popover>
                                </div>
                            );
                        })}
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
};

// Invitation Component
const InvitationDialog: React.FC<{
    workspace: Workspace;
    open: boolean;
    onOpenChange: (open: boolean) => void;
}> = ({ workspace: initialWorkspace, open, onOpenChange }) => {
    const [workspace, setWorkspace] = useState(initialWorkspace);
    const [key, setKey] = useState(initialWorkspace.invitationKey || "");
    const { toast } = useToast();
    const [loading, setLoading] = useState(false);
    const [isShowUrl, setIsShowUrl] = useState(false);

    useEffect(() => {
        setKey(initialWorkspace.invitationKey || "");
        setWorkspace(initialWorkspace);
    }, [initialWorkspace, open]);

    const getFullURL = (k: string) => {
        if (!k) return '';
        return `${Util.getAppUrl()}/ws-invite/${k}`;
    };

    const updateLink = async (newKey: string) => {
        setLoading(true);
        try {
            if (workspace) {
                workspace.invitationKey = newKey;
                await Fire.ws.updateWorkspace(workspace);
                setKey(newKey);
                toast({ title: t`Invitation link updated` });
            }
        } catch (e) {
            toast({ title: t`Failed to update link` });
        } finally {
            setLoading(false);
        }
    };

    const createNewLink = () => {
        const newKey = `${workspace.id}-${UUID.get()}`;
        updateLink(newKey);
    };

    const deleteLink = () => {
        updateLink("");
    };

    const copyToClipboard = async () => {
        const url = getFullURL(key);
        if (!url) return;
        await clipboardWrite(url);
        toast({ title: t`Copied to clipboard` });
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogTrigger asChild>
                <Button>
                    <IconLink className="mr-2 h-4 w-4" /> <Trans>Invite Member</Trans>
                </Button>
            </DialogTrigger>
            <DialogContent className="w-[95vw] sm:max-w-lg p-0 overflow-hidden">
                <div className="p-6 min-w-0">
                    <DialogHeader>
                        <DialogTitle><Trans>Invite Members</Trans></DialogTitle>
                        <DialogDescription asChild>
                            <div className="space-y-2 text-sm">
                                <p>
                                    {getInvitationPolicy(workspace) === 'ALLOW_LISTED_EMAIL_DOMAIN'
                                        ? <Trans>Only users from the allowed domains can join with this link.</Trans>
                                        : <Trans>Anyone with this link can join your workspace.</Trans>}
                                </p>
                                <div className="bg-muted/50 rounded-lg p-3 space-y-1.5 text-xs text-muted-foreground border">
                                    <div className="flex items-start gap-2">
                                        <span className="font-medium text-card-foreground shrink-0"><Trans>Member Policy:</Trans></span>
                                        <span>
                                            {getInvitationPolicy(workspace) === 'ALLOW_LISTED_EMAIL_DOMAIN'
                                                ? workspace.emailDomains?.length > 0
                                                    ? <>{workspace.emailDomains.map(d => <Badge key={d} variant="outline" className="mr-1 text-meta-sm px-1.5 h-4">{d}</Badge>)}</>
                                                    : <span className="text-destructive font-medium"><Trans>No allowed domains set — no one can join as a member. Add domains in Settings.</Trans></span>
                                                : getInvitationPolicy(workspace) === 'ALLOW_ANY_EMAIL_DOMAIN'
                                                    ? <Trans>Public — any email domain</Trans>
                                                    : <Trans>Private — this invitation link only</Trans>
                                            }
                                        </span>
                                    </div>
                                    <div className="flex items-start gap-2">
                                        <span className="font-medium text-card-foreground shrink-0"><Trans>Guest Policy:</Trans></span>
                                        <span>
                                            {workspace.guestInvitationPolicy === 'DISALLOW_ALL'
                                                ? <Trans>Guest invitations disabled</Trans>
                                                : workspace.guestInvitationPolicy === 'RESTRICT_TO_DOMAINS'
                                                    ? workspace.guestDomains?.length > 0
                                                        ? <>{workspace.guestDomains.map(d => <Badge key={d} variant="outline" className="mr-1 text-meta-sm px-1.5 h-4">{d}</Badge>)}</>
                                                        : <Trans>Restricted domains only (none set)</Trans>
                                                    : <Trans>All domains allowed as guest</Trans>
                                            }
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </DialogDescription>
                    </DialogHeader>

                    <div className="space-y-6 py-4 min-w-0 overflow-hidden">
                        {key ? (
                            <div className="space-y-4 min-w-0">
                                <div className="space-y-2 min-w-0">
                                    <Label><Trans>Invitation Link</Trans></Label>
                                    <div
                                        onClick={() => setIsShowUrl(!isShowUrl)}
                                        className="group bg-primary/20 p-3 rounded-md text-xs sm:text-sm text-card-foreground cursor-pointer border border-primary/20 hover:border-primary/30 transition-all w-full overflow-hidden flex items-center justify-between gap-2 min-w-0"
                                    >
                                        <span className={`flex-1 min-w-0 ${!isShowUrl ? 'truncate block' : 'break-all whitespace-normal'}`}>
                                            {getFullURL(key)}
                                        </span>
                                        <div className="flex items-center gap-1 shrink-0">
                                            <div className="text-muted-foreground group-hover:text-primary transition-colors">
                                                {isShowUrl ? <ChevronUp className="w-4 h-4" /> : <IconChevronDown className="w-4 h-4" />}
                                            </div>
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                className="h-7 px-2 gap-1 text-xs text-primary hover:text-primary hover:bg-primary/30"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    copyToClipboard();
                                                }}
                                            >
                                                <Copy className="w-3.5 h-3.5" />
                                                <Trans>Copy</Trans>
                                            </Button>
                                        </div>
                                    </div>
                                </div>

                                <div className="flex flex-col gap-2">
                                    <AlertDialog>
                                        <AlertDialogTrigger asChild>
                                            <Button variant="outline" className="w-full justify-start" disabled={loading}>
                                                <RefreshCw className="mr-2 h-4 w-4 shrink-0" /> <Trans>Regenerate Link</Trans>
                                            </Button>
                                        </AlertDialogTrigger>
                                        <AlertDialogContent>
                                            <AlertDialogHeader>
                                                <AlertDialogTitle><Trans>Create new invitation link?</Trans></AlertDialogTitle>
                                                <AlertDialogDescription>
                                                    <Trans>This will invalidate the current invitation link. Users attempting to join with the old link will get an error.</Trans>
                                                </AlertDialogDescription>
                                            </AlertDialogHeader>
                                            <AlertDialogFooter>
                                                <AlertDialogCancel><Trans>Cancel</Trans></AlertDialogCancel>
                                                <AlertDialogAction onClick={createNewLink}><Trans>Regenerate</Trans></AlertDialogAction>
                                            </AlertDialogFooter>
                                        </AlertDialogContent>
                                    </AlertDialog>

                                    <AlertDialog>
                                        <AlertDialogTrigger asChild>
                                            <Button variant="outline" className="w-full justify-start text-destructive hover:text-destructive hover:bg-destructive/10" disabled={loading}>
                                                <Trash2 className="mr-2 h-4 w-4 shrink-0" /> <Trans>Delete Link</Trans>
                                            </Button>
                                        </AlertDialogTrigger>
                                        <AlertDialogContent>
                                            <AlertDialogHeader>
                                                <AlertDialogTitle><Trans>Delete invitation link?</Trans></AlertDialogTitle>
                                                <AlertDialogDescription>
                                                    <Trans>This will permanently remove the invitation link. No one will be able to join via link until you create a new one.</Trans>
                                                </AlertDialogDescription>
                                            </AlertDialogHeader>
                                            <AlertDialogFooter>
                                                <AlertDialogCancel><Trans>Cancel</Trans></AlertDialogCancel>
                                                <AlertDialogAction onClick={deleteLink} className="bg-destructive hover:bg-destructive"><Trans>Delete</Trans></AlertDialogAction>
                                            </AlertDialogFooter>
                                        </AlertDialogContent>
                                    </AlertDialog>
                                </div>
                            </div>
                        ) : (
                            <div className="text-center py-6">
                                <p className="text-muted-foreground mb-4"><Trans>No active invitation link.</Trans></p>
                                <Button onClick={createNewLink} disabled={loading}>
                                    <Trans>Create Link</Trans>
                                </Button>
                            </div>
                        )}
                    </div>

                    <DialogFooter className="sm:justify-start">
                        <div className="text-xs text-muted-foreground">
                            <Trans>Note: You can restrict allowed email domains in Settings.</Trans>
                        </div>
                    </DialogFooter>
                </div>
            </DialogContent>
        </Dialog>
    );
}

export default WSAdminMembers;
